import React, { useState } from 'react';

import { Link } from 'react-router-dom';
import { trpc } from '../../trpc/client';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Search, Tag, Calendar, User, ArrowRight } from 'lucide-react';

import { Loader2 } from 'lucide-react';

const BlogListPage = () => {

    const [searchTag, setSearchTag] = useState<string | undefined>(undefined);

    // Fetch posts
    const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
        trpc.blog.getPublishedPosts.useInfiniteQuery(
            { limit: 9, tag: searchTag },
            { getNextPageParam: (lastPage) => lastPage.nextCursor }
        );

    const posts = data?.pages.flatMap((page) => page.items) || [];

    return (
        <div className="min-h-screen bg-background flex flex-col transition-colors duration-500">

            {/* Hero Section */}
            {/* Hero Section */}
            <div className="bg-primary/5 border-b border-primary/10 pt-32 pb-20 px-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-primary/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
                <div className="max-w-7xl mx-auto text-center relative z-10">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-6xl font-black mb-6 tracking-tighter text-foreground"
                    >
                        Logistics <span className="text-primary">Insights</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-muted-foreground max-w-2xl mx-auto font-light"
                    >
                        Stay updated with the latest trends, tips, and company announcements.
                    </motion.p>
                </div>
            </div>

            <main className="flex-grow container mx-auto px-4 py-12 max-w-7xl">
                {/* Filter/Search Bar (Simple for now) */}
                {searchTag && (
                    <div className="mb-8 flex items-center justify-center">
                        <span className="bg-primary/10 text-primary border border-primary/20 px-4 py-2 rounded-full flex items-center gap-2">
                            Filtered by: <span className="font-bold">#{searchTag}</span>
                            <button onClick={() => setSearchTag(undefined)} className="hover:text-primary transition-colors ml-2">✕</button>
                        </span>
                    </div>
                )}

                {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="bg-card rounded-2xl border border-border overflow-hidden h-full flex flex-col shadow-sm">
                                <div className="h-48 bg-muted animate-pulse" />
                                <div className="p-6 flex flex-col flex-grow gap-4">
                                    <div className="flex gap-2">
                                        <div className="h-3 w-20 bg-muted animate-pulse rounded" />
                                        <div className="h-3 w-16 bg-muted animate-pulse rounded" />
                                    </div>
                                    <div className="h-6 w-full bg-muted animate-pulse rounded" />
                                    <div className="h-4 w-full bg-muted animate-pulse rounded" />
                                    <div className="h-4 w-2/3 bg-muted animate-pulse rounded" />
                                    <div className="mt-auto h-8 w-24 bg-muted animate-pulse rounded" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        {posts.length === 0 ? (
                            <div className="text-center py-20 flex flex-col items-center justify-center animate-in fade-in zoom-in-95">
                                <div className="w-24 h-24 bg-muted/50 rounded-full flex items-center justify-center mb-6">
                                    <Search className="w-10 h-10 text-muted-foreground opacity-50" />
                                </div>
                                <h3 className="text-2xl font-bold text-foreground mb-2">No articles found</h3>
                                <p className="text-muted-foreground max-w-md mx-auto">
                                    We couldn't find any articles matching your criteria. Try looking for something else or come back later!
                                </p>
                                {searchTag && (
                                    <button
                                        onClick={() => setSearchTag(undefined)}
                                        className="mt-6 text-primary font-bold hover:underline"
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {posts.map((post, index) => (
                                    <motion.article
                                        key={post.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="bg-card text-card-foreground rounded-2xl shadow-sm hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 overflow-hidden flex flex-col h-full border border-border group"
                                    >
                                        {/* Cover Image */}
                                        <Link to={`/blog/${post.slug}`} className="block overflow-hidden h-48 bg-muted relative">
                                            {post.coverImage ? (
                                                <img
                                                    src={post.coverImage}
                                                    alt={post.title}
                                                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground/30">
                                                    <span className="text-4xl font-black">K&C</span>
                                                </div>
                                            )}
                                        </Link>

                                        <div className="p-6 flex flex-col flex-grow">
                                            {/* Meta */}
                                            <div className="flex items-center text-xs text-muted-foreground mb-4 gap-4 uppercase tracking-wider font-medium">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {format(new Date(post.createdAt), 'MMM d, yyyy')}
                                                </span>
                                                {/* Tags */}
                                                {post.tags.length > 0 && (
                                                    <span className="flex items-center gap-1 text-primary">
                                                        <Tag className="w-3 h-3" />
                                                        {post.tags[0]}
                                                    </span>
                                                )}
                                            </div>

                                            <Link to={`/blog/${post.slug}`} className="block group/title">
                                                <h2 className="text-xl font-bold text-foreground mb-3 group-hover/title:text-primary transition-colors line-clamp-2 leading-tight">
                                                    {post.title}
                                                </h2>
                                            </Link>

                                            <p className="text-muted-foreground/80 mb-6 line-clamp-3 text-sm leading-relaxed">
                                                {post.excerpt || post.content.substring(0, 150) + '...'}
                                            </p>

                                            <div className="mt-auto pt-4 flex items-center justify-between border-t border-border">
                                                <div className="flex items-center gap-2">
                                                    {post.author.profile?.profilePicturePath ? (
                                                        <img src={post.author.profile.profilePicturePath} alt={post.author.profile.fullName} className="w-8 h-8 rounded-full object-cover ring-2 ring-background" />
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center ring-2 ring-background">
                                                            <User className="w-4 h-4 text-muted-foreground" />
                                                        </div>
                                                    )}
                                                    <span className="text-xs font-bold text-foreground">
                                                        {post.author.profile?.fullName || post.author.username || 'Admin'}
                                                    </span>
                                                </div>
                                                <Link to={`/blog/${post.slug}`} className="text-primary font-bold text-sm flex items-center gap-1 hover:gap-2 transition-all">
                                                    Read <ArrowRight className="w-4 h-4" />
                                                </Link>
                                            </div>
                                        </div>
                                    </motion.article>
                                ))}
                            </div>
                        )}

                        {/* Pagination / Load More */}
                        {hasNextPage && (
                            <div className="mt-12 text-center">
                                <button
                                    onClick={() => fetchNextPage()}
                                    disabled={isFetchingNextPage}
                                    className="px-8 py-3 bg-card border border-border text-foreground font-semibold rounded-lg hover:bg-muted disabled:opacity-50 transition-colors shadow-sm"
                                >
                                    {isFetchingNextPage ? 'Loading more...' : 'Load More Articles'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
};

export default BlogListPage;
